import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Pagination } from 'components/Pagination/Pagination'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import useQueryPagination from 'hooks/useQueryPagination'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Flex, MainHeadline, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { formatDateForDisplay, getParams, handlePagination } from 'utils/helpers'
import {
    approveInventoryAssignment,
    clearInventoryAssignments,
    declineInventoryAssignment,
    getInventoryAssignments,
} from 'features/inventory/inventoryItemsSlice'
import { Options } from 'components/Options/Options'
import { useNavigate } from 'react-router-dom'
import { setConfirmModal } from 'features/global/globalSlice'
import { ModalTypes } from 'types/global.interface'
import { FilterEnum } from 'components/Filter/SingleFilter'
import { IGetAllInventoryAssignmentsSingleResponse } from 'types/api/inventoryItems.interface'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { OtherText } from 'styling/Typography'
import { ASSIGNMENTS_PAGE_SUBLINE } from '../inventoryControlDemoData'
import { ViewInventoryAssignmentModal } from './ViewInventoryAssignmentModal'

export const InventoryAssignments = () => {
    const { assignments } = useSelector((store: RootState) => store.inventoryItems)
    const [params, setParams] = useState<any>(getParams({ location }))
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const [viewModal, setViewModal] = useState<{ open: boolean, value: IGetAllInventoryAssignmentsSingleResponse }>({ open: false, value: null })

    useQueryPagination({ fetchFunctions: [getInventoryAssignments], cleanUpFunctions: [clearInventoryAssignments], params })

    const handleRequest = (id: number, approve: boolean, item: IGetAllInventoryAssignmentsSingleResponse) => {
        if (item) {
            setViewModal({ open: true, value: item })
            return
        }
        dispatch(
            setConfirmModal({
                isOpen: true,
                onSave: async () => {
                    await dispatch(approve ? approveInventoryAssignment(id) : declineInventoryAssignment(id))
                },
                setParams: {
                    type: ModalTypes.BeforeSabe,
                    f: setParams,
                    noItems: assignments.data.length < 2,
                },
                word: approve ? 'approve this assignment' : 'decline this assignment',
            }),
        )
    }

    const filters = [
        {
            type: FilterEnum.SELECT,
            name: 'Status',
            options: inventoryAssignmentStatuses,
            propName: 'status'
        },
    ]

    const columns: IHonestTableColumn<any>[] = [
        { id: 'type', header: 'TYPE', cell: (item) => item.type == 1 ? 'Assign' : 'Unassign' },
        { id: 'location', header: 'LOCATION', cell: (item) => item.location?.name || '/' },
        {
            id: 'status',
            header: 'STATUS',
            cell: (item) => (
                <StatusPill status={item.status == 1 ? 'warning' : item.status == 2 ? 'success' : item.status == 3 ? 'danger' : 'neutral'}>{inventoryAssignmentStatuses.find(s => s.value == item.status)?.label || '/'}</StatusPill>
            ),
        },
        { id: 'date', header: 'DATE', cell: (item) => formatDateForDisplay(item.createdAt) },
        { id: 'items', header: 'ITEMS', cell: (item) => item.lines?.length || '0' },
        {
            id: 'actions',
            header: '',
            align: 'right',
            cell: (item) => (
                <span onClick={(e) => e.stopPropagation()}>
                    <Options item={item} title='Assignments' openModal={handleRequest} />
                </span>
            ),
        },
    ]

    return <>
        <NavBar enableTemporaryManualClosing filterOptions={filters} visibleFilterOverflow setParams={setParams} />
        <PageWrapper>
            <Flex between center mb='16' >
                <Flex column gap='0.25rem'>
                    <MainHeadline>Inventory Assignments</MainHeadline>
                    <OtherText grey>{ASSIGNMENTS_PAGE_SUBLINE}</OtherText>
                </Flex>
                <Button variant='primary' onClick={() => navigate('add')} >Add Assignment</Button>
            </Flex>
            {!assignments?.data?.length ? (
                <NoItems title='Assignments' />
            ) : (
                <HonestTable
                    columns={columns}
                    data={assignments.data}
                    keyField='id'
                    onClick={(item) => handleRequest(item.id, false, item)}
                />
            )}
            {viewModal.open && <ViewInventoryAssignmentModal value={viewModal.value} closeModal={() => setViewModal({ value: null, open: false })} />}
        </PageWrapper>
        <PageFooter>
            <Pagination
                setPage={(page) => handlePagination(page, setParams, 30)}
                pageCount={Math.ceil(assignments?.totalPages)}
                forcePage={(params.page ? params.page : 1) - 1}
            />
        </PageFooter>
    </>
}

export const inventoryAssignmentStatuses = [
    { label: 'Pending', value: 1 },
    { label: 'Approved', value: 2 },
    { label: 'Declined', value: 3 }
]
